import { Config } from '../../video-data/config';
import type Luminary from '../../';
import type { Asset, BaseAsset } from '../asset/BaseAsset';
import type { TimeAwareMediaAsset } from '../asset/TimeAwareMediaAsset';
import type { Scene } from '../scene/Scene';
import type { CircleMask } from '../mask/CircleMask';
import type { PolygonMask } from '../mask/PolygonMask';
import type { StencilMask } from '../mask/StencilMask';
import { holdConfigCurveAtRest } from '../../libs/AnimationCurveManager';
import type { AnimationConfig } from '../../libs/AnimationCurveManager';
import { observable, runInAction, type IObservableValue } from '../reactivity';

export type MorphableProperty =
    | 'x'
    | 'y'
    | 'scale.x'
    | 'scale.y'
    | 'alpha'
    | 'mask.transition'
    | 'rotation'
    | 'rectWidth'
    | 'rectHeight';

export type MorphLinkedPropertySpecs<T> = Partial<
    Record<
        keyof T & string,
        {
            isEqual?: (a: T, b: T) => boolean;
            sync?: (target: T, source: T) => void;
        }
    >
>;

type MorphSourceState = {
    asset: Asset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    opacity: number;
    rotation: number;
};

export class MorphManager {
    #slide: Scene;

    static #targetToSourceId: WeakMap<BaseAsset, string> = new WeakMap();
    static #sourceToTargetId: WeakMap<BaseAsset, string> = new WeakMap();
    static #mediaTimeOffsets: WeakMap<BaseAsset, IObservableValue<number | null>> = new WeakMap();
    static #updatingLuminaries: WeakSet<Luminary> = new WeakSet();

    static getMediaTimeOffset(asset: BaseAsset): number | null {
        return MorphManager.#mediaTimeOffsetBox(asset).get();
    }

    static #mediaTimeOffsetBox(asset: BaseAsset): IObservableValue<number | null> {
        let offset = MorphManager.#mediaTimeOffsets.get(asset);
        if (!offset) {
            offset = observable.box<number | null>(null);
            MorphManager.#mediaTimeOffsets.set(asset, offset);
        }
        return offset;
    }

    static isMorphTarget(asset: BaseAsset): boolean {
        return MorphManager.#targetToSourceId.has(asset);
    }

    #sourceStates: Map<string, MorphSourceState> = new Map();
    #targetAssets: Set<Asset> = new Set();

    constructor(slide: Scene) {
        this.#slide = slide;
    }

    static #runUnlessAlreadyUpdating(scene: Scene, work: () => void) {
        // A detached scene (mid-destroy) has no luminary, and no siblings to recurse into either.
        const luminary = scene.root as Luminary | undefined;
        if (!luminary) {
            work();
            return;
        }

        if (MorphManager.#updatingLuminaries.has(luminary)) {
            return;
        }

        MorphManager.#updatingLuminaries.add(luminary);
        try {
            work();
        } finally {
            MorphManager.#updatingLuminaries.delete(luminary);
        }
    }

    setupMorphAnimations() {
        const nextScene = this.#slide.nextScene;
        if (!nextScene) {
            return;
        }

        MorphManager.#runUnlessAlreadyUpdating(this.#slide, () => {
            const sourceAssets = this.#slide
                .getAssetsAtSceneEnd()
                .sort((a, b) => a.name.localeCompare(b.name));
            const targetAssets = nextScene
                .getAssetsAtSceneStart()
                .sort((a, b) => a.name.localeCompare(b.name));

            sourceAssets.forEach((sourceAsset) => {
                const matchingAsset = targetAssets.find(
                    (targetAsset) =>
                        !this.#targetAssets.has(targetAsset) &&
                        sourceAsset.isMorphMatch(targetAsset),
                );
                if (!matchingAsset) {
                    return;
                }

                this.#setupMorphForAssetPair(sourceAsset, matchingAsset);
            });

            for (const { asset } of this.#sourceStates.values()) {
                if (!asset.hasMorphAnimation) {
                    continue;
                }
                MorphManager.#forceBuildAndDraw(asset);
            }
        });
    }

    clearMorphAnimations() {
        MorphManager.#runUnlessAlreadyUpdating(this.#slide, () => {
            for (const state of this.#sourceStates.values()) {
                const { asset, x, y, scaleX, scaleY, opacity, rotation } = state;

                if (!asset || asset.destroyed) {
                    continue;
                }

                asset.clearMorphAnimations();

                asset.transformation.position.x = x;
                asset.transformation.position.y = y;
                asset.scale.x = scaleX;
                asset.scale.y = scaleY;
                asset.opacity = opacity;
                asset.transformation.rotation = rotation;

                if (asset.mask && Config.isStencilMask(asset.mask)) {
                    (asset.mask as CircleMask | PolygonMask)._clearMorphState();
                }

                MorphManager.#forceBuildAndDraw(asset);
            }
            this.#clearPairings();
        });
    }

    resetMorphAnimations() {
        MorphManager.#runUnlessAlreadyUpdating(this.#slide, () => {
            for (const { asset } of this.#sourceStates.values()) {
                if (!asset || asset.destroyed) {
                    continue;
                }
                asset.clearMorphAnimations();
            }
            this.#clearPairings();
        });
    }

    #clearPairings() {
        for (const { asset } of this.#sourceStates.values()) {
            MorphManager.#sourceToTargetId.delete(asset);
        }
        this.#sourceStates.clear();

        for (const targetAsset of this.#targetAssets) {
            MorphManager.#targetToSourceId.delete(targetAsset);
        }
        runInAction(() => {
            for (const targetAsset of this.#targetAssets) {
                MorphManager.#mediaTimeOffsetBox(targetAsset).set(null);
            }
        });
        for (const targetAsset of this.#targetAssets) {
            if (!targetAsset.destroyed) {
                MorphManager.#forceBuildAndDraw(targetAsset);
            }
        }
        this.#targetAssets.clear();
    }

    static #resolveMediaTimeOffset(
        sourceAsset: Asset,
        targetAsset: Asset,
        sceneDuration: number,
    ): number {
        if (Config.isMediaAsset(sourceAsset) && Config.isMediaAsset(targetAsset)) {
            const source = sourceAsset as TimeAwareMediaAsset;
            const target = targetAsset as TimeAwareMediaAsset;
            if (source.isVideo && target.isVideo) {
                const sourceEndTimeSeconds = source.sourceTimeAt(sceneDuration);
                if (sourceEndTimeSeconds !== null) {
                    return sourceEndTimeSeconds * 1000 - target.startTime;
                }
            }
        }

        const inheritedOffset = MorphManager.getMediaTimeOffset(sourceAsset) ?? 0;
        return inheritedOffset + sceneDuration;
    }

    #setupMorphForAssetPair(sourceAsset: Asset, targetAsset: Asset) {
        const { duration: morphDuration, interpolation } = sourceAsset._morphParameters;

        if (this.#slide.duration < morphDuration) {
            return;
        }

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                opacity: sourceAsset.opacity,
                rotation: sourceAsset.transformation.rotation,
            });
        }

        this.#targetAssets.add(targetAsset);
        MorphManager.#sourceToTargetId.set(sourceAsset, targetAsset.id);
        MorphManager.#targetToSourceId.set(targetAsset, sourceAsset.id);

        if (
            Config.isMediaAsset(targetAsset) ||
            Config.isIconAsset(targetAsset) ||
            Config.isLottieAsset(targetAsset)
        ) {
            MorphManager.#mediaTimeOffsetBox(targetAsset).set(
                MorphManager.#resolveMediaTimeOffset(
                    sourceAsset,
                    targetAsset,
                    this.#slide.duration,
                ),
            );
        }

        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const alphaOnly = sourceAsset.morphAlphaOnly;
        const animationConfigs = (() => {
            const assetConfigs = MorphManager.#buildAssetAnimationConfigs(sourceAsset, targetAsset);

            const hasMaskMorph =
                !alphaOnly && MorphManager.#setupMaskMorphState(sourceAsset, targetAsset);
            if (!hasMaskMorph) {
                return assetConfigs;
            }

            const maskConfigs = MorphManager.#buildMaskAnimationConfigs(sourceAsset, targetAsset);
            return { ...assetConfigs, ...maskConfigs };
        })();
        const excludedProperties = sourceAsset.morphExcludedProperties;
        Object.entries(animationConfigs).forEach(([morphableProperty, config]) => {
            if (alphaOnly && morphableProperty !== 'alpha') {
                return;
            }
            if (!alphaOnly && excludedProperties.includes(morphableProperty as MorphableProperty)) {
                return;
            }

            const sourceAnim = sourceAsset.addMorphAnimation();
            sourceAnim.property = config.property;
            sourceAnim.startValue = config.startValue;
            sourceAnim.endValue = config.endValue;
            sourceAnim.duration = morphDuration;
            sourceAnim.interpolation =
                config.property === 'alpha' && sourceAsset.morphAlphaStep
                    ? Config.STEP_INTERPOLATION_PRESET
                    : interpolation;
            sourceAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;
        });
    }

    static #getXPosition(asset: Asset) {
        return asset.x;
    }

    static #getYPosition(asset: Asset) {
        return asset.y;
    }

    static #buildAssetAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        if (sourceAsset.morphAlphaStep && sourceAsset.morphAlphaOnly) {
            // A match-cut alpha step runs between fixed endpoints — a stand-in appears 0->1, the
            // out-scene's own content disappears 1->0 — never keyed to the asset's live alpha.
            const appearing = sourceAsset.isMatchCutDuplicate;
            configs['alpha'] = {
                property: 'alpha',
                shouldOffsetPropertyValue: false,
                startValue: appearing ? 0 : 1,
                endValue: appearing ? 1 : 0,
            };
        } else if (sourceAsset.opacity !== targetAsset.opacity) {
            configs['alpha'] = {
                property: 'alpha',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.opacity,
                endValue: targetAsset.opacity,
            };
        }

        if (sourceAsset.transformation.rotation !== targetAsset.transformation.rotation) {
            configs['rotation'] = {
                property: 'rotation',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.transformation.rotation,
                endValue: targetAsset.transformation.rotation,
            };
        }

        // Asset with Mask uses other properties for scale and position morph
        if (sourceAsset.mask) {
            return configs;
        }

        const sourceX = MorphManager.#getXPosition(sourceAsset);
        const targetX = MorphManager.#getXPosition(targetAsset);
        if (sourceX !== targetX) {
            configs['x'] = {
                property: 'x',
                shouldOffsetPropertyValue: false,
                startValue: sourceX,
                endValue: targetX,
            };
        }

        const sourceY = MorphManager.#getYPosition(sourceAsset);
        const targetY = MorphManager.#getYPosition(targetAsset);
        if (sourceY !== targetY) {
            configs['y'] = {
                property: 'y',
                shouldOffsetPropertyValue: false,
                startValue: sourceY,
                endValue: targetY,
            };
        }

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        if (scaleRatioX !== 1) {
            configs['scale.x'] = {
                property: 'scale.x',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioX,
            };
        }

        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;
        if (scaleRatioY !== 1) {
            configs['scale.y'] = {
                property: 'scale.y',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioY,
            };
        }

        return configs;
    }

    static #buildMaskAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        configs['mask.transition'] = {
            property: 'mask.transition',
            shouldOffsetPropertyValue: false,
            startValue: 0,
            endValue: 1,
        };

        const sourceX = MorphManager.#getXPosition(sourceAsset);
        const targetX = MorphManager.#getXPosition(targetAsset);
        if (sourceX !== targetX) {
            configs['x'] = {
                property: 'x',
                shouldOffsetPropertyValue: false,
                startValue: sourceX,
                endValue: targetX,
            };
        }

        const sourceY = MorphManager.#getYPosition(sourceAsset);
        const targetY = MorphManager.#getYPosition(targetAsset);
        if (sourceY !== targetY) {
            configs['y'] = {
                property: 'y',
                shouldOffsetPropertyValue: false,
                startValue: sourceY,
                endValue: targetY,
            };
        }

        const bothAreMediaAssets =
            Config.isMediaAsset(sourceAsset) && Config.isMediaAsset(targetAsset);
        if (bothAreMediaAssets) {
            // The same scale the morph uses when there is no mask, but measured from the size the
            // media is drawn at rather than from the asset's bounds, which a mask clips.
            const sourceDrawn = sourceAsset._displaySize();
            const targetDrawn = targetAsset._displaySize();

            // Both sides are guarded because an unloaded media measures NaN, which would otherwise
            // reach the curve and blank the asset.
            const scaleRatioX =
                sourceDrawn.width > 0 && targetDrawn.width > 0
                    ? targetDrawn.width / sourceDrawn.width
                    : 1;
            if (scaleRatioX !== 1) {
                configs['scale.x'] = {
                    property: 'scale.x',
                    shouldOffsetPropertyValue: false,
                    startValue: 1,
                    endValue: scaleRatioX,
                };
            }

            const scaleRatioY =
                sourceDrawn.height > 0 && targetDrawn.height > 0
                    ? targetDrawn.height / sourceDrawn.height
                    : 1;
            if (scaleRatioY !== 1) {
                configs['scale.y'] = {
                    property: 'scale.y',
                    shouldOffsetPropertyValue: false,
                    startValue: 1,
                    endValue: scaleRatioY,
                };
            }
        }

        const bothAreRectangleAssets =
            Config.isRectangleAsset(sourceAsset) && Config.isRectangleAsset(targetAsset);
        if (bothAreRectangleAssets) {
            if (sourceAsset.rectWidth !== targetAsset.rectWidth) {
                configs['rectWidth'] = {
                    property: 'rectWidth',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceAsset.rectWidth,
                    endValue: targetAsset.rectWidth,
                };
            }
            if (sourceAsset.rectHeight !== targetAsset.rectHeight) {
                configs['rectHeight'] = {
                    property: 'rectHeight',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceAsset.rectHeight,
                    endValue: targetAsset.rectHeight,
                };
            }
        }

        return configs;
    }

    static #setupMaskMorphState(sourceAsset: Asset, targetAsset: Asset): boolean {
        const sourceMask = sourceAsset.mask as StencilMask;
        const targetMask = targetAsset.mask as StencilMask;

        if (!sourceMask || !targetMask) {
            return false;
        }

        if (Config.isCircleMask(sourceMask) && Config.isCircleMask(targetMask)) {
            const sourceCircle = sourceMask as CircleMask;
            const targetCircle = targetMask as CircleMask;

            sourceCircle._setMorphState({
                startRadius: sourceCircle.endRadius,
                endRadius: targetCircle.endRadius,
                startCenter: { x: sourceCircle.centerX, y: sourceCircle.centerY },
                endCenter: { x: targetCircle.centerX, y: targetCircle.centerY },
            });
            sourceCircle.transition = 0;
            return true;
        }

        if (Config.isPolygonMask(sourceMask) && Config.isPolygonMask(targetMask)) {
            const sourcePolygon = sourceMask;
            const targetPolygon = targetMask;

            if (sourcePolygon.endPoints.length !== targetPolygon.endPoints.length) {
                return false;
            }

            const orderedSource = MorphManager.#orderPolygonCornersByCentroid(
                sourcePolygon.endPoints,
                sourcePolygon._getCornerRadiusPerPoint('endPoints'),
            );
            const orderedTarget = MorphManager.#orderPolygonCornersByCentroid(
                targetPolygon.endPoints,
                targetPolygon._getCornerRadiusPerPoint('endPoints'),
            );

            if (orderedSource.points.some(isNaN) || orderedTarget.points.some(isNaN)) {
                return false;
            }

            sourcePolygon._setMorphState({
                startPoints: orderedSource.points,
                endPoints: orderedTarget.points,
                startCornerRadii: orderedSource.cornerRadii,
                endCornerRadii: orderedTarget.cornerRadii,
            });
            sourcePolygon.transition = 0;
            return true;
        }

        return false;
    }

    static #orderPolygonCornersByCentroid(
        points: readonly number[],
        cornerRadii: readonly number[],
    ): { points: number[]; cornerRadii: number[] } {
        const numPoints = points.length / 2;

        let centroidX = 0;
        let centroidY = 0;
        for (let i = 0; i < numPoints; i++) {
            centroidX += points[i * 2];
            centroidY += points[i * 2 + 1];
        }
        centroidX /= numPoints;
        centroidY /= numPoints;

        const pointsArray = Array.from({ length: numPoints }, (_, i) => {
            const x = points[i * 2];
            const y = points[i * 2 + 1];
            const angle = Math.atan2(y - centroidY, x - centroidX);
            return { x, y, angle, cornerRadius: cornerRadii[i] };
        });

        pointsArray.sort((a, b) => a.angle - b.angle);

        return {
            points: pointsArray.flatMap((point) => [point.x, point.y]),
            cornerRadii: pointsArray.map((point) => point.cornerRadius),
        };
    }

    static areLinkedPropertiesEqual<T>(a: T, b: T, specs: MorphLinkedPropertySpecs<T>): boolean {
        type Overrides = MorphLinkedPropertySpecs<T>[keyof T & string];
        return Object.entries(specs).every(([name, rawOverrides]) => {
            const prop = name as keyof T & string;
            const overrides = rawOverrides as Overrides;
            const isEqual = overrides?.isEqual ?? ((x: T, y: T) => x[prop] === y[prop]);
            return isEqual(a, b);
        });
    }

    static syncLinkedProperties<T>(
        target: T,
        source: T,
        specs: MorphLinkedPropertySpecs<T>,
    ): boolean {
        type Overrides = MorphLinkedPropertySpecs<T>[keyof T & string];
        let changed = false;
        for (const [name, rawOverrides] of Object.entries(specs)) {
            const prop = name as keyof T & string;
            const overrides = rawOverrides as Overrides;
            const isEqual = overrides?.isEqual ?? ((x: T, y: T) => x[prop] === y[prop]);
            if (isEqual(target, source)) continue;
            const sync =
                overrides?.sync ??
                ((t: T, s: T) => ((t as Record<string, unknown>)[prop] = s[prop]));
            sync(target, source);
            changed = true;
        }
        return changed;
    }

    static updateMorphAnimations(scenes: readonly Scene[]) {
        const hasActiveTextEditing = scenes.some((scene) =>
            scene.getAssetsByType(Config.TEXT_ASSET_TYPE).some((a) => a.hasTextEditingEnabled),
        );
        if (hasActiveTextEditing) return;

        scenes.forEach((scene) => {
            scene._resetMorphAnimations();
        });

        scenes.forEach((scene) => {
            if (scene.morphEnabled) {
                scene._setupMorphAnimations();
            }
        });
    }

    static updateAdjacentMorphAnimations(modifiedScene: Scene) {
        const hasActiveTextEditing = modifiedScene.root.scenes.some((scene) =>
            scene.getAssetsByType(Config.TEXT_ASSET_TYPE).some((a) => a.hasTextEditingEnabled),
        );
        if (hasActiveTextEditing) return;

        let chainHead = modifiedScene;
        while (chainHead.prevScene?.morphEnabled) {
            chainHead = chainHead.prevScene as Scene;
        }

        const chain: Scene[] = [];
        for (
            let scene: Scene | undefined = chainHead;
            scene;
            scene = scene.morphEnabled ? (scene.nextScene as Scene | undefined) : undefined
        ) {
            chain.push(scene);
        }

        chainHead.prevScene?._resetMorphAnimations();
        chain.forEach((scene) => scene._resetMorphAnimations());
        chain.forEach((scene) => {
            if (scene.morphEnabled) {
                scene._setupMorphAnimations();
            }
        });
    }

    static propagateLinkedProperties(modifiedScene: Scene) {
        MorphManager.#runUnlessAlreadyUpdating(modifiedScene, () => {
            let current = modifiedScene;
            while (current.morphEnabled && current.nextScene) {
                MorphManager.#propagateBetweenScenes(current, current.nextScene);
                current = current.nextScene;
            }

            current = modifiedScene;
            while (current.prevScene?.morphEnabled) {
                MorphManager.#propagateBetweenScenes(current, current.prevScene);
                current = current.prevScene;
            }
        });
    }

    static #propagateBetweenScenes(fromScene: Scene, toScene: Scene) {
        const fromAssets = fromScene.getAssets({ deep: true });
        const toAssetMap = new Map(toScene.getAssets({ deep: true }).map((a) => [a.id, a]));

        for (const fromAsset of fromAssets) {
            const targetId = MorphManager.#sourceToTargetId.get(fromAsset);
            if (targetId) {
                const target = toAssetMap.get(targetId);
                if (target?.syncMorphLinkedProperties(fromAsset)) {
                    target.requiresRedraw = true;
                }
            }

            const sourceId = MorphManager.#targetToSourceId.get(fromAsset);
            if (sourceId) {
                const source = toAssetMap.get(sourceId);
                if (source?.syncMorphLinkedProperties(fromAsset)) {
                    source.requiresRedraw = true;
                }
            }
        }
    }

    static #forceBuildAndDraw(asset: Asset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }

    static modifyNonMorphCurve(
        config: AnimationConfig,
        hasMorphLeadInAnimation: boolean,
        hasMorphAnimation: boolean,
    ) {
        if (hasMorphLeadInAnimation && hasMorphAnimation) {
            holdConfigCurveAtRest(config);
            return;
        }
        if (hasMorphAnimation) {
            const wouldRemoveBothEnds =
                config.removeAnchoredPoints === Config.START_ANIMATION_CURVE_ANCHOR;
            if (wouldRemoveBothEnds) {
                holdConfigCurveAtRest(config);
                return;
            }
            config.removeAnchoredPoints = Config.END_ANIMATION_CURVE_ANCHOR;
            return;
        }
        if (hasMorphLeadInAnimation) {
            const wouldRemoveBothEnds =
                config.removeAnchoredPoints === Config.END_ANIMATION_CURVE_ANCHOR;
            if (wouldRemoveBothEnds) {
                holdConfigCurveAtRest(config);
                return;
            }
            config.removeAnchoredPoints = Config.START_ANIMATION_CURVE_ANCHOR;
        }
    }
}
